import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheck } from 'react-icons/fi';
import SectionHeading from '../ui/SectionHeading';
import Reveal from '../ui/Reveal';
import { getPackages } from '../../lib/api';
import { withVat, formatKES } from '../../lib/tax';

const FALLBACK_PACKAGES = [
  { Id: 1, PackageName: 'Individual', Amount: 550 },
  { Id: 3, PackageName: 'Individual Advanced', Amount: 1850 },
  { Id: 2, PackageName: 'Enterprise', Amount: 3700 },
  { Id: 4, PackageName: 'Enterprise Advanced', Amount: 6300 },
];

const PLAN_META = {
  Individual: {
    accountType: 'individual',
    tagline: 'Private, unlimited text chat with the Piua AI assistant.',
    features: ['Unlimited text chat', 'Unlimited conversation history', 'Custom AI agents', 'Export as PDF or JSON'],
    highlight: false,
  },
  'Individual Advanced': {
    accountType: 'individual',
    tagline: 'Everything in Individual, plus document analysis and image generation.',
    features: [
      'Everything in Individual',
      'Read & write (document analysis)',
      'AI image generation',
      'Custom AI agents',
      'API integration',
    ],
    highlight: true,
  },
  Enterprise: {
    accountType: 'organization',
    tagline: 'A managed organization account for up to 15 team members.',
    features: [
      'Up to 15 team members',
      'Centralized user management',
      'Role-based access',
      'Custom AI agents',
      'Org-wide usage & privacy reporting',
    ],
    highlight: false,
  },
  'Enterprise Advanced': {
    accountType: 'organization',
    tagline: 'Everything in Enterprise, with unlimited team members.',
    features: [
      'Unlimited team members',
      'Everything in Enterprise',
      'Custom AI agents',
      'API integration',
      'Priority support',
    ],
    highlight: true,
  },
};

function PlanCard({ pkg, index }) {
  const meta = PLAN_META[pkg.PackageName] || {};
  const { total } = withVat(pkg.Amount);

  return (
    <Reveal delay={index * 0.08} className="h-full">
      <div
        className={`flex h-full flex-col gap-6 rounded-2xl border p-8 shadow-card transition hover:-translate-y-1 ${
          meta.highlight
            ? 'border-brand-300 bg-brand-gradient-soft dark:border-brand-700'
            : 'border-line bg-white dark:border-midnight-600 dark:bg-midnight-700'
        }`}
      >
        {meta.highlight && (
          <span className="w-fit rounded-full bg-brand-gradient px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
            Most popular
          </span>
        )}
        <div>
          <h3 className="text-lg font-bold text-ink dark:text-white">{pkg.PackageName}</h3>
          <p className="mt-1 text-sm text-muted dark:text-subtle">{meta.tagline}</p>
        </div>

        <div>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-extrabold text-ink dark:text-white">{formatKES(pkg.Amount)}</span>
            <span className="text-sm text-subtle">/month</span>
          </div>
          <p className="mt-1 text-xs text-subtle">{formatKES(total)} incl. 16% VAT</p>
        </div>

        <ul className="flex flex-1 flex-col gap-2.5">
          {(meta.features || []).map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm text-muted dark:text-subtle">
              <FiCheck className="mt-0.5 flex-none text-success" size={16} />
              {feature}
            </li>
          ))}
        </ul>

        <Link
          to={`/signup?type=${meta.accountType}&package=${pkg.Id}`}
          className={meta.highlight ? 'btn-primary w-full' : 'btn-secondary w-full'}
        >
          Choose {pkg.PackageName} <FiArrowRight />
        </Link>
      </div>
    </Reveal>
  );
}

export default function PricingSection() {
  const [packages, setPackages] = useState(FALLBACK_PACKAGES);

  useEffect(() => {
    let active = true;
    getPackages()
      .then((data) => {
        if (active && Array.isArray(data) && data.length > 0) setPackages(data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const individualPlans = packages.filter((p) => PLAN_META[p.PackageName]?.accountType === 'individual');
  const orgPlans = packages.filter((p) => PLAN_META[p.PackageName]?.accountType === 'organization');

  return (
    <section id="pricing" className="scroll-mt-24 py-24">
      <div className="container-page flex flex-col items-center gap-16">
        <SectionHeading
          eyebrow="Plans & pricing"
          title="Simple, transparent pricing"
          description="All plans are billed monthly in Kenyan Shillings (KES) and can be upgraded, downgraded, or cancelled at any time — no long-term contracts."
        />

        <div className="flex w-full flex-col gap-6">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-subtle">For individuals</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {individualPlans.map((pkg, index) => (
              <PlanCard key={pkg.Id} pkg={pkg} index={index} />
            ))}
          </div>
        </div>

        <div className="flex w-full flex-col gap-6">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-subtle">For organizations</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {orgPlans.map((pkg, index) => (
              <PlanCard key={pkg.Id} pkg={pkg} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
